// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ListItem, { UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import {
    InTheBoxSpesification,
    SpecialSpesification,
} from "@sc/Overrides/Widgets/ProductDetails/ProductDetailsSpecifications";
import DownArrow from "@sc/XDL/Icons/DownArrow";
import { createLinkIconStyles } from "@sc/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsFeaturesStyles {
    title?: TypographyPresentationProps;
    data?: InjectableCss;
    showMoreLink?: LinkPresentationProps;
}

export const featuresStyles: ProductDetailsFeaturesStyles = {
    title: {
        variant: "h4",
        forwardAs: "p",
        css: css`
            margin-bottom: 12px;
        `,
    },
    data: {
        css: css`
            font-size: 16px;
            line-height: 26px;

            ul {
                margin-top: 0;
                &,
                li:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    showMoreLink: {
        ...createLinkIconStyles(DownArrow, {
            icon: {
                position: "right",
                iconProps: {
                    size: 13,
                    css: css`
                        &,
                        svg {
                            width: 9px;
                        }
                    `,
                },
            },

            css: css`
                margin-top: 12px;
            `,
        }),
    },
};

const styles = featuresStyles;

const ProductDetailsFeatures: React.FC<Props> = ({ product: { specifications } }) => {
    const otherSpecifications = specifications?.filter(
        specification =>
            specification.name.toLowerCase() !== InTheBoxSpesification &&
            specification.name.toLowerCase() !== SpecialSpesification,
    );

    if (!otherSpecifications || otherSpecifications.length === 0) {
        return null;
    }

    const doScroll = () => {
        const selector = document.querySelector("[data-test-selector='productDetails_specifications_wrapper']");

        if (selector) {
            selector.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    };

    return (
        <StyledWrapper>
            <Typography {...styles.title}>{translate("Features")}</Typography>
            <StyledWrapper {...styles.data}>
                <UnorderedList>
                    {otherSpecifications?.map((specification: any, index: any) => {
                        if (index > 1) {
                            return null;
                        }
                        return (
                            <ListItem key={`${index}_${specification.id.toString()}`}>
                                {parse(transformContentHtml(specification.htmlContent), parserOptions)}
                            </ListItem>
                        );
                    })}
                </UnorderedList>
            </StyledWrapper>

            {otherSpecifications.length > 2 && (
                <Link aria-label={translate("Show More")} {...styles.showMoreLink} onClick={() => doScroll()}>
                    {translate("Show More")}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsFeatures),
    definition: {
        displayName: "Features",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
